import { FieldParser, ObjectShape, ParseResult, UnionBranchShape, ValidationWarning } from './types';
import { REJECT, unknownValue, type Reject } from './primitives';

/**
 * The whole of `optional` and `nullable`. Each owns one **sentinel** — the raw
 * value it accepts as intended, which is also the value it falls back to — and
 * delegates everything else to the inner parser.
 *
 * That is the entire difference between them, which is why they **commute**:
 * for a `T | null | undefined` field, `optional(nullable(p))` and
 * `nullable(optional(p))` are the same two filters in the other order, and
 * neither warns about a value the other would accept.
 *
 * It substitutes the sentinel in two cases, and records it both times, because
 * any defaulting is a degradation:
 *
 * - the inner could not produce a value at all;
 * - the inner drifted **at this level**, so the value it did produce is its own
 *   default — and this combinator's sentinel is the more honest one, since the
 *   field is declared as admitting it.
 *
 * A `dropped` record is neither: it says the inner array kept the rows it could
 * read, so substituting the sentinel would throw away every row that WAS
 * readable.
 *
 * A REFUSAL is neither either, and is the one case it does not recover from: a
 * field given `REJECT` has declared that it has no substitute, and the sentinel
 * is a substitute. So `nullable(num(REJECT))` means "null is fine, else it must
 * be a number" and `optional(num(REJECT))` means "may be absent, but if present
 * it must be a number". An explicit `withDefault(inner, value)` is different and
 * still applies: there the substitute is named at the site by the author, rather
 * than invented here.
 *
 * The recorded warning rewrites the inner's when there is one here — its
 * `usedDefault` described a fallback that did not survive — and is written
 * fresh when the cause was recorded deeper, where nothing yet says that THIS
 * field was replaced.
 */
const widenTo = <T, S>(inner: FieldParser<T>, sentinel: S): FieldParser<T | S> => {
  const substitute = (warnings: Map<string, ValidationWarning>, path: string, received: unknown): ParseResult<S> => {
    const own = warnings.get(path);
    warnings.set(path, own !== undefined ? { ...own, usedDefault: sentinel } : { path, expected: 'a readable value', received, usedDefault: sentinel });
    return { ok: true, value: sentinel };
  };
  return (raw, path, warnings) => {
    if (raw === (sentinel as unknown)) return { ok: true, value: sentinel };
    const r = inner(raw, path, warnings);
    const own = warnings.get(path);
    // A REFUSAL is not an ordinary failure: the field has declared it has no
    // substitute, and this combinator's sentinel is a substitute like any other.
    // Propagating is what makes `nullable(num(REJECT))` mean what it reads as —
    // "null is fine, else it must be a number" — instead of being inert.
    if (own !== undefined && own.refused === true) return r;
    if (!r.ok || (own !== undefined && own.dropped === undefined)) return substitute(warnings, path, raw);
    return r;
  };
};

/**
 * `optional(inner)` → `T | undefined`: a **missing key** is the intended value.
 * Use for a wire field the backend omits when empty. See `widenTo` for the
 * shared rule, including why it commutes with `nullable`.
 */
export const optional = <T>(inner: FieldParser<T>): FieldParser<T | undefined> => widenTo(inner, undefined);

/**
 * `nullable(inner)` → `T | null`: an explicit **`null`** is the intended value.
 * Use for a field the backend always sends, sometimes as `null`.
 *
 * A missing key is NOT special-cased — it reaches the inner, drifts there, and
 * comes back as the substituted `null`, so `nullable(str())` on an absent field
 * still yields `null` with a warning. Delegating rather than special-casing is
 * what makes the pair commute.
 */
export const nullable = <T>(inner: FieldParser<T>): FieldParser<T | null> => widenTo(inner, null);

/**
 * Substitutes `[]` for a non-array wire, with a warning at this position: "no
 * rows" is the only value a list has to fall back on, and it is what 40 call
 * sites used to supply by hand with a `withDefault` wrapper.
 *
 * `REJECT` in the default slot refuses a non-array instead of substituting —
 * for a list whose absence would be read as a fact rather than as a gap. It
 * says nothing about the rows: a list that lost one is still a list, so this
 * never fires for a drop. Whether a short list is usable is the consumer's
 * question, answered by which `Received` arm it accepts.
 *
 * Per-item parsing; items that return `ok: false` are dropped, and **the drop
 * is recorded** at this array's own path. Nothing recorded it before, which is
 * what made a dropped row invisible: its warnings stay in the map under
 * `path[i]…`, indistinguishable from those of a row that was kept with a
 * defaulted field. A consumer can now say "3 of 42 rows could not be read"
 * instead of quietly showing 39. Defaulted items are kept.
 */
export const array =
  <T>(item: FieldParser<T>, onNonArray: T[] | Reject = []): FieldParser<T[]> =>
  (raw, path, warnings) => {
    if (!Array.isArray(raw)) {
      warnings.set(path, {
        path,
        expected: 'array',
        received: raw,
        usedDefault: onNonArray === REJECT ? undefined : onNonArray,
        ...(onNonArray === REJECT ? { refused: true as const } : {}),
      });
      return onNonArray === REJECT ? { ok: false } : { ok: true, value: onNonArray };
    }
    const value: T[] = [];
    raw.forEach((it, i) => {
      const r = item(it, `${path}[${i}]`, warnings);
      if (r.ok) value.push(r.value);
      // ok:false items are dropped; why each one went is already in the map
      // under its indexed path, and the count below says that it went at all.
    });
    if (value.length !== raw.length) {
      warnings.set(path, {
        path,
        expected: 'every row readable',
        received: raw,
        usedDefault: value,
        dropped: { kept: value.length, of: raw.length },
      });
    }
    return { ok: true, value };
  };

/**
 * Structural-only: returns `ok: false` on a non-object/null/array wire (with
 * a warning at this position). Iterates fields; inner `ok: false` propagates
 * so failures at deeper levels still bubble up. Extra fields on the wire are
 * silently ignored.
 */
export const object =
  <T extends object>(shape: ObjectShape<T>): FieldParser<T> =>
  (raw, path, warnings) => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      warnings.set(path, {
        path,
        expected: 'object',
        received: raw,
        usedDefault: undefined,
      });
      return { ok: false };
    }
    const source = raw as Record<keyof T, unknown>;
    // Mutable scratch: `-readonly` strips readonly so we can assign into it
    // (the result `T` may have readonly props), `?` makes `{}` a valid start,
    // and `T[P]` keeps the per-field assignment type-checked. The boundary
    // cast below bridges scratch -> T.
    const value: { -readonly [P in keyof T]?: T[P] } = {};
    for (const key of Object.keys(shape) as Array<keyof T>) {
      const fieldParser = shape[key];
      const subPath = path === '' ? String(key) : `${path}.${String(key)}`;
      const r = fieldParser(source[key], subPath, warnings);
      if (!r.ok) return { ok: false };
      value[key] = r.value;
    }
    return { ok: true, value: value as T };
  };

const describeForNote = (v: unknown): string => {
  if (typeof v === 'string') return JSON.stringify(v);
  if (v === null) return 'null';
  if (v === undefined) return 'undefined';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return typeof v;
};

/**
 * Maps a successfully-parsed `T` to a `U`. `fn` returns `undefined` to signal
 * "can't map this", which becomes `ok: false`. On that path, if `inner`
 * already drifted at the same path, the transform's failure is appended to
 * the existing warning's `notes` (the inner's drift is the root cause); if
 * `inner` was clean, a fresh warning is written.
 *
 * Apply `optional`/`nullable` *outside* a transform (e.g.
 * `optional(pureTransform(...))`): `U` should be a concrete type, with
 * missing/null handled by the wrapping combinator.
 */
export const pureTransform =
  <T, U>(inner: FieldParser<T>, fn: (value: T) => U | undefined): FieldParser<U> =>
  (raw, path, warnings) => {
    const r = inner(raw, path, warnings);
    if (!r.ok) return { ok: false };
    const mapped = fn(r.value);
    if (mapped === undefined) {
      const note = `transform could not map ${describeForNote(r.value)}`;
      const own = warnings.get(path);
      if (own !== undefined) {
        warnings.set(path, {
          ...own,
          notes: [...(own.notes ?? []), note],
        });
      } else {
        warnings.set(path, {
          path,
          expected: 'a value the transform can map',
          received: r.value,
          usedDefault: undefined,
          notes: [note],
        });
      }
      return { ok: false };
    }
    return { ok: true, value: mapped };
  };

/**
 * Recovers a failure (`ok: false`) to `fallback`, keeping the warnings that
 * explain why it failed. Also rewrites the path's `usedDefault` to
 * `fallback`, so the warning reflects the value actually substituted (rather
 * than an inner primitive's earlier default).
 *
 * Catches failures, not defaults: `withDefault(str(), 'x')` is a no-op because
 * `str` never fails (it defaults to `''`), and wrapping `array` is one too
 * because `array` substitutes `[]` itself. It only bites on failure-producing
 * inners — `pureTransform`, `object`, `union`, and anything given `REJECT`.
 */
export const withDefault =
  <T>(inner: FieldParser<T>, fallback: T): FieldParser<T> =>
  (raw, path, warnings) => {
    const r = inner(raw, path, warnings);
    if (r.ok) return r;
    const own = warnings.get(path);
    if (own !== undefined) {
      warnings.set(path, { ...own, usedDefault: fallback });
    }
    return { ok: true, value: fallback };
  };

/**
 * Ergonomic composition of `pureTransform` + `withDefault`: map `T` to `U`,
 * falling back to `fallback` (with a warning) if either the mapping or the
 * inner parse fails.
 */
export const transform = <T, U>(inner: FieldParser<T>, fn: (value: T) => U | undefined, fallback: U): FieldParser<U> =>
  withDefault(pureTransform(inner, fn), fallback);

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isNaN(value) ? undefined : value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

/**
 * Marks the warning this parser left at its own path as a refusal, so that
 * `widenTo` propagates it instead of substituting its sentinel.
 *
 * NOTE: needed only where a REJECT is expressed by a combinator rather than by
 * a primitive's default slot. The primitives write `refused` themselves; a
 * `pureTransform` cannot, because it does not know whether the caller asked for
 * a refusal or simply has no fallback to offer.
 */
const markRefused =
  <T>(inner: FieldParser<T>): FieldParser<T> =>
  (raw, path, warnings) => {
    const r = inner(raw, path, warnings);
    if (r.ok) return r;
    const own = warnings.get(path);
    if (own !== undefined) warnings.set(path, { ...own, refused: true });
    return r;
  };

/**
 * Accepts a number or a numeric string and normalizes to `number`, defaulting
 * to `fallback` (with a warning) on anything else. Postgres serializes
 * `bigint`/`numeric`/`COUNT(*)` as strings, so list endpoints routinely send
 * counts and ids as numeric strings. Use `numeric(REJECT)` to reject a
 * non-numeric value instead of defaulting.
 */
export const numeric = (fallback: number | Reject = 0): FieldParser<number> =>
  fallback === REJECT ? markRefused(pureTransform(unknownValue(), toNumber)) : transform(unknownValue(), toNumber, fallback);

/**
 * Discriminated-union parser. `tag` names a string- or boolean-typed field on
 * `T` whose literal value selects the branch; `branches` maps each literal to
 * an `ObjectShape` describing the non-discriminator fields of that branch
 * (the discriminator is omitted via `Omit` — its value is asserted by this
 * parser at runtime and merged back into the result). A boolean tag's branches
 * are keyed `true` and `false`.
 *
 * Returns `ok: false` when (a) `raw` is not an object, (b) the discriminator
 * is missing or of the wrong type, or (c) the discriminator value names no
 * branch. The type is fixed by the branch keys: a union whose branches are
 * all `true`/`false` takes a boolean and refuses the strings `'true'` and
 * `'false'`; any other union takes a string and refuses a boolean.
 * Inside an `array(...)` that failure drops the row; inside an `object(...)`
 * it propagates so the enclosing record fails too. There is deliberately no
 * default branch — a record whose discriminator the frontend doesn't know
 * is uninterpretable, and inventing a fallback would silently mis-route the
 * other fields.
 */
export const union = <T extends Record<K, string | boolean>, K extends keyof T & string>(
  tag: K,
  branches: { [V in T[K] as `${V}`]: UnionBranchShape<T, K, V> }
): FieldParser<T> => {
  const branchParsers: Record<string, FieldParser<object>> = {};
  for (const key of Object.keys(branches)) {
    branchParsers[key] = object((branches as Record<string, ObjectShape<object>>)[key]);
  }
  const branchKeys = Object.keys(branches);
  const tagType = branchKeys.every((key) => key === 'true' || key === 'false') ? 'boolean' : 'string';
  return (raw, path, warnings) => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      warnings.set(path, {
        path,
        expected: `discriminated union on ${tag}`,
        received: raw,
        usedDefault: undefined,
      });
      return { ok: false };
    }
    const tagValue = (raw as Record<string, unknown>)[tag];
    const tagPath = path === '' ? tag : `${path}.${tag}`;
    if (typeof tagValue !== tagType || !Object.prototype.hasOwnProperty.call(branchParsers, String(tagValue))) {
      warnings.set(tagPath, {
        path: tagPath,
        expected: `one of: ${branchKeys.join(', ')}`,
        received: tagValue,
        usedDefault: undefined,
      });
      return { ok: false };
    }
    const branchResult = branchParsers[String(tagValue)](raw, path, warnings);
    if (!branchResult.ok) return { ok: false };
    return {
      ok: true,
      value: { ...branchResult.value, [tag]: tagValue } as T,
    };
  };
};
